import React, { Fragment, useState } from 'react';
import { Button }from '@cmsgov/design-system';

import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import ModalHeader from '@common-components/headers/ModalHeader';
import UniversalInput from '@lib/wrappers/UniversalInput';

// Utils

import { normalizeAddress } from '@utils/normalize/normalizeAddress';


// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { STATE_CODES } from '@constants/common/statesList';
import { householdMemberTools } from '@utils/tools/householdMemberTools';



const ValidateAddressModal = props => {

    const {
        actions: {
            changeData,
            hideModal
        },
        bottomAnchors: [
            anchor_1,
        ],
        boundAnchor,
        data,
        formName,
        modal: {
            modalData: {
                callback = undefined,
                data: {
                    input: {
                        zipcode,
                        addressLine1,
                        addressLine2,
                        state,
                        city
                    },
                    suggestions: serverSuggestions
                },
                stateCode
            }
        },
        modalHeaderProps,
        modalHeaderProps: {
            modalId
        }
    } = props;

    const handleHideClick = () => hideModal();

    // Here we create local state for user address values ("You entered" on UI):

    const [ userInput, checkUserInput ] = useState({
        address: normalizeAddress({
            postalCode: zipcode,
            streetAddress1: addressLine1,
            streetAddress2: addressLine2,
            state: STATE_CODES.find(st => st.stateCode === state).stateName,
            city: city
        }),
        values: {
            postalCode: zipcode,
            streetAddress1: addressLine1,
            streetAddress2: addressLine2,
            state: state,
            city: city
        },
        isUserInputChecked: false
    });

    // Here we create local state for API suggestions ("We found" on UI):

    const [ machineSuggestions, checkSuggestion] = useState(
        serverSuggestions ? serverSuggestions.map((suggestion, i) => ({
            address: normalizeAddress({
                postalCode: suggestion.zipcode,
                streetAddress1: suggestion.addressLine1,
                streetAddress2: suggestion.addressLine2,
                state: STATE_CODES.find(state => state.stateCode === suggestion.state).stateName,
                city: suggestion.city
            }),
            values: {
                primaryAddressCountyFipsCode: typeof suggestion.countyFips === 'string' ? suggestion.countyFips : null,
                county: suggestion.countyName,
                postalCode: suggestion.zipcode,
                streetAddress1: suggestion.addressLine1,
                streetAddress2: suggestion.addressLine2,
                state: suggestion.state,
                city: suggestion.city,
                countyCode: typeof suggestion.countyFips === 'string' ? suggestion.countyFips : null
            },
            [`isSuggestion${i}Checked`]: false
        })) : []
    );

    /**
     *
     * @param {Object} newData - updated (checked) machine suggestion
     *
     * Here we check/uncheck needed machine suggestion and if we have one of them checked
     * we uncheck rest of machine suggestions and user input address as well
     */
    const onFieldChange_suggestions = newData => {
        checkSuggestion(machineSuggestions.map((suggestion, i) =>
            suggestion.address === newData.address ?
                newData :
                {
                    ...suggestion,
                    [`isSuggestion${i}Checked`]: false
                }
        ));
        if(machineSuggestions.find((suggestion, i) => suggestion[`isSuggestion${i}Checked`])) {
            checkUserInput(userInput => ({
                ...userInput,
                isUserInputChecked: false
            }));
        }
    };

    /**
     *
     * @param {Object} newData - updated (checked) user input
     *
     * Here we check/uncheck user input address, and if it is checked we uncheck all machine suggestions
     */
    const onFieldChange_userInput = newData => {

        checkUserInput(state => ({
            ...state,
            ...newData
        }));

        if(userInput.isUserInputChecked && machineSuggestions.length !== 0) {
            checkSuggestion(suggestions => suggestions.map((suggestion, i) => ({
                ...suggestion,
                [`isSuggestion${i}Checked`]: false
            })));
        }
    };

    /**
     * Here we resolve situation:
     *
     * 1. To save suggestion if our user checked it
     *  or
     * 2. Not to save if nothing was checked
     *
     */
    const handleConfirmationClick = () => {
        const checkedMachineSuggestion = machineSuggestions.find((suggestion, i) => suggestion[`isSuggestion${i}Checked`]);

        const valueToSave =
            userInput.isUserInputChecked ?
                userInput.values :                      // If User agrees with address he/she entered in Form on Page
                checkedMachineSuggestion ?
                    checkedMachineSuggestion.values :   // If User chose one of suggestions
                    null;                               // None of inputs being checked

        if(callback) {
            callback(valueToSave);
            handleHideClick();
            return null;
        }

        if(valueToSave) {

            const newData = { ...data };
            
            const primaryContact = householdMemberTools.getPrimaryContact(newData.taxHousehold.householdMember);
            
            const updatedHouseholdContact = {
                ...primaryContact.householdContact,
                homeAddress: {
                    ...primaryContact
                        .householdContact
                        .homeAddress,
                    ...valueToSave
                },
                ...(valueToSave.state === stateCode ? { outOfStateAttestation: false } : {})
            };

            newData
                .taxHousehold
                .householdMember =
                    householdMemberTools
                        .updatePrimaryContact(newData.taxHousehold.householdMember, { householdContact: updatedHouseholdContact });

            changeData(newData);
            handleHideClick();
        } else {
            handleHideClick();
        }
    };

    return (
        <Fragment>
            <div className="gi-modal__header__head">
                <ModalHeader { ...modalHeaderProps } />
            </div>
            <div className="gi-modal__body txt-left" id = 'validateAddressModal'>
                <Form { ...{ formName } } >
                    <Fragment>
                        <div className="gi-modal__subheading">
                            <h3 data-testid={`${modalId}_body_header`}><FormattedMessage id='label.youEntered'/></h3>
                            <div className="gi-modal__address__list">
                                <UniversalInput
                                    label={{
                                        labelClasses: ['ui', 'gi-checkbox'],
                                        text: 'common.input.label.non_internationalized_text',
                                        labelTextReplacements: {
                                            text: userInput.address
                                        }
                                    }}
                                    inputData={{
                                        isInputRequired: false,
                                        type: INPUT_TYPES.CHECKBOX,
                                        name: 'isUserInputChecked',
                                        checked: userInput.isUserInputChecked,
                                        value: userInput.isUserInputChecked,
                                        currentData: userInput,
                                        fields: []
                                    }}
                                    inputActions={{
                                        onFieldChange: onFieldChange_userInput
                                    }}
                                />
                            </div>
                        </div>
                        {
                            machineSuggestions && machineSuggestions.length === 0  &&

                            <div className='usa-alert gi-alert gi-alert__left-accent gi-alert__error margin-t-20' role='alert'>
                                <div className='gi-alert__body'>
                                    <p className='gi-alert__text' data-testid={`${modalId}_body_alert`}>
                                        <FormattedMessage id='modals.validate_address.address_not_recognized'/>
                                        <br/>
                                        <FormattedMessage id='modals.validate_address.click_ok'/>
                                    </p>
                                </div>
                            </div>
                        }
                    </Fragment>

                    {/******************************************
                     Begin - If #machineSuggestions (not empty)
                     ******************************************/}

                    {
                        machineSuggestions && machineSuggestions.length !== 0  &&

                        <Fragment>
                            <div className="gi-modal__subheading margin-t-20">
                                <h3 data-testid={`${modalId}_body_subheading`}><FormattedMessage id='label.weFound'/></h3>
                                {
                                    machineSuggestions && machineSuggestions.map((suggestion, i) => {
                                        return (
                                            <div className="gi-modal__address__list" key={`${suggestion.address}_${i}`}>
                                                <UniversalInput
                                                    label={{
                                                        labelClasses: ['ui', 'gi-checkbox'],
                                                        text: 'common.input.label.non_internationalized_text',
                                                        labelTextReplacements: {
                                                            text: suggestion.address
                                                        }
                                                    }}
                                                    inputData={{
                                                        isInputRequired: false,
                                                        type: INPUT_TYPES.CHECKBOX,
                                                        name: `isSuggestion${i}Checked`,
                                                        checked: suggestion[`isSuggestion${i}Checked`],
                                                        value: suggestion[`isSuggestion${i}Checked`],
                                                        currentData: suggestion,
                                                        fields: []
                                                    }}
                                                    inputActions={{
                                                        onFieldChange: onFieldChange_suggestions
                                                    }}
                                                />
                                            </div>
                                        );
                                    })
                                }
                            </div>
                            <div className='usa-alert gi-alert gi-alert__left-accent gi-alert__error margin-t-20' role='alert'>
                                <div className='gi-alert__body'>
                                    <p className='gi-alert__text' data-testid={`${modalId}_body_alert`}>
                                        <FormattedMessage id='modals.validate_address.make_choice'/>
                                    </p>
                                </div>
                            </div>
                        </Fragment>
                    }

                    {/******************************************
                     End - If #machineSuggestions (not empty)
                     ******************************************/}
                </Form>
            </div>
            <div className="gi-modal__footer">
                <div className="grid-row controls">
                    <div className="grid-offset-4 txt-left">
                        <Button
                            className='usa-button usa-button--outline gi-button gi-button__outline'
                            data-testid={`${modalId}-cancel_btn`}
                            onClick={handleHideClick}
                        >
                            <FormattedMessage id='button.cancel'/>
                        </Button>
                        <Button
                            className='usa-button gi-button gi-button__primary'
                            data-testid={`${modalId}-ok_btn`}
                            onClick={handleConfirmationClick}
                            inputRef={boundAnchor(anchor_1)}
                        >
                            <FormattedMessage id='button.ok'/>
                        </Button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default ValidateAddressModal;
